"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Copy, Check, ExternalLink, ArrowRight, Loader2 } from "lucide-react";
import { useAffiliate } from "@/hooks/useAffiliate";

interface ReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TIER_EXAMPLES = [
  { tier: "Pro", price: 50, commission: 20 },
  { tier: "Pro", price: 100, commission: 40 },
  { tier: "Elite", price: 200, commission: 80 },
];

export function ReferralModal({ open, onOpenChange }: ReferralModalProps) {
  const { enrolled, referralCode, referralLink, totalEarned, totalReferrals, enroll, isLoading } = useAffiliate();
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  const handleCopy = async (text: string, type: "code" | "link") => {
    await navigator.clipboard.writeText(text);
    if (type === "code") {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } else {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    console.log("[REFERRAL] Starting enrollment...");
    try {
      const result = await enroll();
      console.log("[REFERRAL] Enrollment success:", result);
    } catch (e: any) {
      console.error("[REFERRAL] Enrollment failed:", e);
      const msg = e?.message || "";
      if (msg.includes("409") || msg.includes("already registered")) {
        setEnrollError("Your email is already registered as an affiliate. Visit xynth.tolt.io to manage your account.");
      } else {
        setEnrollError("Failed to enroll. Please try again or contact support.");
      }
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden border border-neutral-800 bg-black text-white">
        <VisuallyHidden.Root>
          <DialogTitle>Referral Program</DialogTitle>
          <DialogDescription>Earn cash by referring friends to Xynth</DialogDescription>
        </VisuallyHidden.Root>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : !enrolled ? (
          /* ── Not enrolled: explain + CTA ── */
          <div className="flex flex-col">
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-lg font-semibold tracking-tight text-white">Earn up to $80 USD cash per referral</h2>
              <p className="text-sm text-neutral-400 mt-1">
                You earn <span className="text-white font-medium">40% of first-month revenue</span> for every friend who subscribes to Xynth.
              </p>
            </div>

            {/* Tier breakdown */}
            <div className="px-6 py-2">
              <div className="rounded-md border border-neutral-800 overflow-hidden text-xs">
                <div className="grid grid-cols-3 text-[10px] font-medium text-neutral-600 uppercase tracking-wider px-2.5 py-1.5 bg-neutral-900 border-b border-neutral-800">
                  <span>Plan</span>
                  <span className="text-center">Price</span>
                  <span className="text-right">You earn</span>
                </div>
                {TIER_EXAMPLES.map((ex, i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-3 px-2.5 py-1.5 ${
                      i < TIER_EXAMPLES.length - 1 ? "border-b border-neutral-900" : ""
                    }`}
                  >
                    <span className="text-neutral-500">
                      {ex.tier}
                      {ex.price === 100 ? " (yr)" : ex.price === 200 ? "" : " (mo)"}
                    </span>
                    <span className="text-center text-neutral-500">${ex.price}/mo</span>
                    <span className={`text-right font-semibold ${i === TIER_EXAMPLES.length - 1 ? "text-white" : "text-neutral-300"}`}>
                      ${ex.commission} <span className="text-[9px] font-normal text-neutral-600">USD</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="px-6 pb-4 pt-1">
              <p className="text-xs text-neutral-500">
                Your referrals get <span className="text-neutral-300">20% off their first month</span>. You&apos;ll receive an email to set up payouts after claiming your code.
              </p>
            </div>

            {/* Error message */}
            {enrollError && (
              <div className="px-6 pb-2">
                <p className="text-xs text-red-400">{enrollError}</p>
              </div>
            )}

            {/* CTA */}
            <div className="px-6 pb-6">
              <Button
                onClick={handleEnroll}
                disabled={enrolling}
                className="w-full h-10 bg-white hover:bg-neutral-100 text-black font-medium transition-all"
              >
                {enrolling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Get My Referral Code
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* ── Enrolled: show code + stats ── */
          <div className="flex flex-col">
            <div className="px-6 pt-6 pb-1">
              <h2 className="text-lg font-semibold tracking-tight text-white">Your Referral Dashboard</h2>
            </div>

            {/* Portal CTA */}
            <div className="px-6 py-3">
              <a
                href="https://xynth.tolt.io"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg bg-neutral-800 border border-neutral-700 px-4 py-3 hover:bg-neutral-700/80 transition-colors"
              >
                <p className="text-sm text-neutral-300">To see your earnings and get paid out, visit the <span className="text-white font-medium">affiliate portal</span></p>
                <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1">xynth.tolt.io <ExternalLink className="h-3 w-3" /></p>
              </a>
            </div>

            {/* Code */}
            <div className="px-6 py-2 space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">Promo Code</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 font-mono text-sm tracking-wide text-white">
                  {referralCode}
                </div>
                <button
                  onClick={() => referralCode && handleCopy(referralCode, "code")}
                  className="shrink-0 p-2 rounded-md border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 transition-colors"
                >
                  {copiedCode ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5 text-neutral-400" />}
                </button>
              </div>
            </div>

            {/* Link */}
            <div className="px-6 py-2 space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">Referral Link</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm truncate text-neutral-400">
                  {referralLink}
                </div>
                <button
                  onClick={() => referralLink && handleCopy(referralLink, "link")}
                  className="shrink-0 p-2 rounded-md border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 transition-colors"
                >
                  {copiedLink ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5 text-neutral-400" />}
                </button>
              </div>
            </div>

            <div className="pb-4" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
